import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Tag as TagIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AccountSettings } from "@/components/AccountSettings";

export default function DashboardPage() {
  const { user } = useAuth();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTag, setNewTag] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/my/tags");
      setTags(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const claim = async (e) => {
    e.preventDefault();
    const id = newTag.trim().toUpperCase();
    if (!id) return;
    try {
      await api.post(`/tags/${id}/claim`);
      toast.success(`Tag ${id} claimed`);
      setNewTag("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14" data-testid="dashboard-page">
      <p className="text-xs tracking-[0.25em] uppercase font-bold text-muted-foreground">
        Signed in as {user?.email}
      </p>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-3">
        Your tags
      </h1>

      <form onSubmit={claim} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-md">
        <Input
          data-testid="dashboard-claim-input"
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          placeholder="Tag ID to claim"
          className="h-12 rounded-none bg-[#141414] border-border focus:ring-2 focus:ring-primary text-white uppercase"
        />
        <Button
          type="submit"
          data-testid="dashboard-claim-button"
          className="h-12 rounded-none bg-primary hover:bg-[#FF5C53] tracking-[0.15em] uppercase text-xs font-bold gap-2 px-6"
        >
          <Plus className="w-4 h-4" /> Claim
        </Button>
      </form>

      {loading ? (
        <p className="mt-12 text-xs tracking-[0.2em] uppercase text-muted-foreground">Loading</p>
      ) : tags.length === 0 ? (
        <div
          data-testid="dashboard-empty-state"
          className="mt-12 border border-border bg-[#141414] p-10 text-center"
        >
          <TagIcon className="w-8 h-8 text-primary mx-auto" />
          <h3 className="font-display text-2xl font-black tracking-tight mt-5">No tags yet</h3>
          <p className="mt-3 text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">
            Claim your first tag ID above, or open a tag URL like /tag/DEMO123 and claim it there.
          </p>
        </div>
      ) : (
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {tags.map((t, i) => (
            <Link
              key={t.tag_id}
              to={`/tag/${t.tag_id}`}
              data-testid={`dashboard-tag-card-${t.tag_id}`}
              className="h-full border border-border bg-[#141414] p-6 block rise hover:border-primary hover:-translate-y-1"
              style={{ animationDelay: `${i * 70}ms`, transition: "transform .2s ease, border-color .2s ease" }}
            >
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-primary">
                {t.tag_id}
              </p>
              <h3 className="font-display text-2xl font-black tracking-tight mt-4">
                {t.profile?.name || "Unnamed profile"}
              </h3>
              <div className="mt-5 flex items-center justify-between text-sm text-muted-foreground">
                <span>Blood: {t.profile?.blood_type || "—"}</span>
                <span>{t.profile?.contacts?.length || 0} contacts</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      <AccountSettings title="Your account" />
    </div>
  );
}
